import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  Droplets,
  AlertTriangle,
  Info,
  Activity,
  Shield,
  Syringe,
  Heart
} from 'lucide-react';

export default function LactateSodiumPage() {
  const sectionsData = [
    {
      id: 'forme',
      number: '01',
      title: 'Forme et Présentation',
      icon: Syringe,
      color: 'text-cyan-400',
      content: {
        titre: 'Flacon de 250 ml',
        details: [
          'IV stricte',
          'Sodium = 1000 mmol/l',
          'Lactate = 1000 mmol/l'
        ]
      }
    },
    {
      id: 'indications',
      number: '02',
      title: 'Indications',
      icon: Heart,
      color: 'text-green-400',
      items: [
        'Intoxications par tricycliques avec élargissement du QRS > 0,12 seconde ou troubles du rythme ou défaillance hémodynamique',
        'Complications des intoxications aux β-bloquants (cf. protocole)'
      ]
    },
    {
      id: 'contre-indications',
      number: '03',
      title: 'Contre-indications',
      icon: AlertTriangle,
      color: 'text-red-400',
      items: [
        { label: 'Etats d\'alcalose, acidoses respiratoires', severity: 'high' },
        { label: 'Rétention hydro-sodée', severity: 'high' },
        { label: 'Hyperosmolarité plasmatique', severity: 'high' },
        { label: 'Insuffisance cardiaque', severity: 'critical' },
        { label: 'Syndromes œdémato-ascitiques', severity: 'high' }
      ]
    },
    {
      id: 'classe',
      number: '04',
      title: 'Classe Pharmacologique & Effets',
      icon: Info,
      color: 'text-purple-400',
      content: {
        classe: 'Soluté alcalinisant'
      }
    },
    {
      id: 'effets-indesirables',
      number: '05',
      title: 'Effets Indésirables',
      icon: AlertTriangle,
      color: 'text-amber-400',
      items: [
        'Risque d\'hypokaliémie'
      ]
    },
    {
      id: 'surdosage',
      number: '06',
      title: 'Surdosage',
      icon: AlertTriangle,
      color: 'text-orange-400',
      items: [
        'Surcharge hydro-sodée',
        'Hypokaliémie',
        'Hyperexcitabilité neuromusculaire',
        'Dépression respiratoire par alcalose métabolique'
      ]
    },
    {
      id: 'a-savoir',
      number: '07',
      title: 'À Savoir',
      icon: Info,
      color: 'text-blue-400',
      content: {
        infos: [
          '1 ml = 20 gouttes',
          '33 gouttes/minute = 100 ml en 1 heure'
        ]
      }
    },
    {
      id: 'precautions',
      number: '08',
      title: 'Précautions & Surveillance',
      icon: Shield,
      color: 'text-cyan-400',
      items: [
        { label: 'Injection sous surveillance ECG continu', critical: true },
        { label: 'Ajouter 2 à 4 g de KCl pour 500 ml de lactate', critical: true }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-[hsl(var(--bg))] text-[hsl(var(--text))] p-4 md:p-6" data-testid="lactate-sodium-page">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center space-y-3">
          <div className="flex items-center justify-center gap-3">
            <Droplets className="w-10 h-10 text-blue-400" />
            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-blue-400 to-cyan-300 bg-clip-text text-transparent">
              Lactate de sodium molaire à 112 ‰
            </h1>
          </div>
          <p className="text-[hsl(var(--text-dim))] text-sm md:text-base max-w-3xl mx-auto">
            Guide complet : indications, précautions et surveillance
          </p>
        </div>

        {/* Alerte principale */}
        <Alert className="bg-amber-500/10 border-amber-500/30">
          <AlertTriangle className="w-4 h-4 text-amber-400" />
          <AlertDescription className="text-[hsl(var(--text))]">
            <p className="font-semibold mb-1">⚠️ Soluté alcalinisant - IV stricte</p>
            <p className="text-sm text-[hsl(var(--text-dim))]">
              Injection sous surveillance ECG continue obligatoire. Ajouter systématiquement du KCl.
            </p>
          </AlertDescription>
        </Alert>

        {/* Informations principales en cards */}
        <div className="grid md:grid-cols-2 gap-4">
          {/* Composition */}
          <Card className="bg-[hsl(var(--bg-elev))] border-cyan-500/30" data-testid="composition-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-cyan-300">
                <Droplets className="w-5 h-5" />
                Composition
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/30">
                <p className="text-sm font-semibold text-[hsl(var(--text))] mb-2">Flacon de 250 ml - IV stricte</p>
                <div className="space-y-1 text-xs text-[hsl(var(--text-dim))]">
                  <div className="flex justify-between">
                    <span>Sodium :</span>
                    <span className="font-semibold text-cyan-300">1000 mmol/l</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Lactate :</span>
                    <span className="font-semibold text-cyan-300">1000 mmol/l</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* À Savoir */}
          <Card className="bg-[hsl(var(--bg-elev))] border-blue-500/30" data-testid="savoir-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-blue-300">
                <Info className="w-5 h-5" />
                À Savoir
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/30 space-y-2">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-blue-400"></span>
                  <span className="text-sm text-[hsl(var(--text))]">1 ml = 20 gouttes</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-blue-400"></span>
                  <span className="text-sm text-[hsl(var(--text))]">33 gouttes/min = 100 ml en 1h</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Alerte Précautions critiques */}
        <Alert className="bg-red-500/10 border-red-500/30">
          <Shield className="w-4 h-4 text-red-400" />
          <AlertDescription>
            <p className="font-semibold text-[hsl(var(--text))] mb-2">Précautions critiques :</p>
            <ul className="space-y-1 text-sm text-[hsl(var(--text-dim))]">
              <li className="flex items-start gap-2">
                <span className="text-red-400 mt-0.5">•</span>
                <span>Injection sous surveillance ECG continu</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-400 mt-0.5">•</span>
                <span>Ajouter <span className="font-semibold text-red-300">2 à 4 g de KCl</span> pour 500 ml de lactate</span>
              </li>
            </ul>
          </AlertDescription>
        </Alert>

        {/* Accordéons pour les autres sections */}
        <Card className="bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-cyan-300">
              <Info className="w-5 h-5" />
              Informations Détaillées
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Accordion type="multiple" className="space-y-4">
              {sectionsData.map((section) => {
                const SectionIcon = section.icon;
                
                return (
                  <AccordionItem 
                    key={section.id} 
                    value={section.id}
                    className="border border-[hsl(var(--border))] rounded-lg overflow-hidden bg-[hsl(var(--bg))]"
                    data-testid={`section-${section.id}`}
                  >
                    <AccordionTrigger className="px-4 py-3 hover:bg-[hsl(var(--bg-elev))] transition-colors">
                      <div className="flex items-center gap-3">
                        <Badge className="bg-cyan-500 text-white font-bold">{section.number}</Badge>
                        <SectionIcon className={`w-5 h-5 ${section.color}`} />
                        <span className="font-semibold text-[hsl(var(--text))]">{section.title}</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pb-4">
                      <div className="mt-3">
                        {/* Content avec titre et détails (Forme) */}
                        {section.content && section.content.titre && (
                          <div className="p-4 rounded-lg bg-cyan-500/10 border border-cyan-500/30">
                            <p className="font-semibold text-[hsl(var(--text))] mb-2">{section.content.titre}</p>
                            <ul className="space-y-1 text-sm text-[hsl(var(--text-dim))]">
                              {section.content.details?.map((detail, idx) => (
                                <li key={idx} className="flex items-center gap-2">
                                  <span className="w-1.5 h-1.5 rounded-full bg-cyan-500"></span>
                                  {detail}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Content avec classe (Classe pharmacologique) */}
                        {section.content && section.content.classe && (
                          <div className="p-4 rounded-lg bg-purple-500/10 border border-purple-500/30">
                            <p className="text-[hsl(var(--text))] font-semibold">{section.content.classe}</p>
                          </div>
                        )}

                        {/* Content avec infos (À Savoir) */}
                        {section.content && section.content.infos && (
                          <div className="space-y-2">
                            {section.content.infos.map((info, idx) => (
                              <div key={idx} className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/30">
                                <p className="text-sm text-[hsl(var(--text))]">{info}</p>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Listes */}
                        {section.items && (
                          <ul className="space-y-2">
                            {section.items.map((item, idx) => {
                              const isObject = typeof item === 'object';
                              const label = isObject ? item.label : item;
                              const severity = isObject ? item.severity : null;
                              const critical = isObject ? item.critical : false;
                              
                              return (
                                <li 
                                  key={idx}
                                  className={`flex items-start gap-2 p-3 rounded-lg ${
                                    severity === 'critical' ? 'bg-red-500/10 border border-red-500/30' :
                                    severity === 'high' ? 'bg-amber-500/10 border border-amber-500/30' :
                                    critical ? 'bg-amber-500/10 border border-amber-500/30' :
                                    'bg-[hsl(var(--bg-elev))]'
                                  }`}
                                >
                                  <span className="mt-1 flex-shrink-0 w-1.5 h-1.5 rounded-full bg-cyan-500" />
                                  <span className="text-sm text-[hsl(var(--text-dim))]">{label}</span>
                                  {(severity === 'critical' || critical) && (
                                    <Badge variant="outline" className="ml-auto border-red-500/30 text-red-300 text-xs">
                                      Critique
                                    </Badge>
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
